import { ColumnDef } from '@tanstack/react-table'
import { useTranslations } from '@/hooks/useTranslations'
import { useMemo } from 'react'

import { UserDropdown, UserStatus } from '@/constants/user'
import { useLanguage } from '@/hooks'
import * as DateUtil from '@/utils/date'
import { formatTableNumber } from '@/utils/number'

import { ActionDropdown } from './ActionDropdown'
import { UserStatusBadge } from './UserStatusBadge'
import { Profile } from '@/modules/user/services/user'
import { SizeStatusBadge } from '../../SizeManagement/List/SizeStatusBadge'

type UseDatatableHeadersProps = {
  search: string
  onClickUserDropdownEvent: (eventName: UserDropdown, data: Profile) => void
}

export function useDatatableHeaders({ search, onClickUserDropdownEvent }: UseDatatableHeadersProps) {
  const t = useTranslations('admin.user_management.table')

  const { locale } = useLanguage()

  const columns = useMemo(() => {
    const columnsRender: ColumnDef<Profile>[] = [
      {
        id: 'no',
        header: t('no'),
        cell: (ctx) => (
          <div className="text-start">
            {formatTableNumber({
              index: ctx.row.index + 1,
              pageSize: ctx.table.getState().pagination.pageSize,
              pageIndex: ctx.table.getState().pagination.pageIndex,
            })}
          </div>
        ),
        size: 60,
        enableSorting: false,
        meta: {
          align: 'center',
        },
      },
      {
        id: 'username',
        header: t('username'),
        cell: (ctx) =>
          ctx.row.original?.username ?? "-",
        size: 160,
        enableSorting: false,
      },
      {
        id: 'name',
        header: t('name'),
        cell: (ctx) =>
          ctx.row.original?.firstname || ctx.row.original?.lastname
            ? `${ctx.row.original?.firstname} ${ctx.row.original?.lastname}`
            : "-",
        size: 200,
        enableSorting: false,
      },
      {
        id: 'role',
        header: t('role'),
        cell: (ctx) => ctx.row.original.role_name ?? "-",
        size: 160,
        enableSorting: false,
      },
      {
        id: 'status',
        header: t('status'),
        cell: (ctx) => <SizeStatusBadge status={ctx.row.original.status as UserStatus} />,
        size: 200,
        enableSorting: false,
        meta: {
          align: 'center',
        },
      },
      {
        id: 'action',
        header: '',
        cell: (ctx) => (
          <ActionDropdown
            isDefault={ctx.row.original.is_dev}
            status={ctx.row.original.status as UserStatus}
            onClickDropdownItem={(eventName) => onClickUserDropdownEvent(eventName, ctx.row.original)}
          />
        ),
        size: 60,
        enableSorting: false,
      },
    ]
    return columnsRender
  }, [locale, t, search, onClickUserDropdownEvent])

  return { columns }
}
